import type { Logger } from './logger'
import type { HttpTransport } from './transport'
import type { QueuedEvent, IngestRequest } from './types'
import { isBrowser, isoTimestamp } from './utils'

export interface EventQueueConfig {
  flushAt: number
  flushInterval: number
  transport: HttpTransport
  logger: Logger
  beforeSend?: (event: QueuedEvent) => QueuedEvent | null
}

export class EventQueue {
  private queue: QueuedEvent[] = []
  private timer: ReturnType<typeof setInterval> | null = null
  private config: EventQueueConfig
  private listeners: Array<{ target: EventTarget; event: string; handler: EventListener }> = []
  private closed = false

  constructor(config: EventQueueConfig) {
    this.config = config
    this.startTimer()
    this.registerLifecycleHooks()
  }

  add(event: QueuedEvent): void {
    if (this.closed) {
      return
    }

    const processed = this.config.beforeSend ? this.config.beforeSend(event) : event
    if (!processed) {
      this.config.logger.debug('Event dropped by beforeSend middleware')
      return
    }

    this.queue.push(processed)
    this.config.logger.debug('Event queued:', processed.type, processed.name ?? '')

    if (this.queue.length >= this.config.flushAt) {
      void this.flush()
    }
  }

  async flush(): Promise<void> {
    if (this.queue.length === 0) {
      return
    }

    const events = this.queue.splice(0, this.config.flushAt)
    const payload: IngestRequest = {
      sent_at: isoTimestamp(),
      events,
    }

    this.config.logger.debug(`Flushing ${events.length} events`)
    await this.config.transport.send(payload)
  }

  async close(timeout?: number): Promise<void> {
    if (this.closed) {
      return
    }
    this.closed = true

    this.stopTimer()
    this.removeAllListeners()

    if (this.queue.length > 0) {
      if (timeout !== undefined) {
        await Promise.race([this.flushAll(), this.sleep(timeout)])
      } else {
        await this.flushAll()
      }
    }
  }

  size(): number {
    return this.queue.length
  }

  private async flushAll(): Promise<void> {
    while (this.queue.length > 0) {
      await this.flush()
    }
  }

  private startTimer(): void {
    if (!isBrowser()) {
      return
    }
    this.timer = setInterval(() => {
      void this.flush()
    }, this.config.flushInterval)
  }

  private stopTimer(): void {
    if (this.timer !== null) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  private registerLifecycleHooks(): void {
    if (!isBrowser()) {
      return
    }

    const handleUnload = () => {
      if (this.queue.length === 0) {
        return
      }

      const payload: IngestRequest = {
        sent_at: isoTimestamp(),
        events: this.queue.splice(0),
      }

      this.config.transport.sendBeacon(payload)
    }

    this.addListener(document, 'visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        handleUnload()
      }
    })

    this.addListener(window, 'pagehide', () => {
      handleUnload()
    })
  }

  private addListener(target: EventTarget, event: string, handler: EventListener): void {
    target.addEventListener(event, handler)
    this.listeners.push({ target, event, handler })
  }

  private removeAllListeners(): void {
    for (const { target, event, handler } of this.listeners) {
      target.removeEventListener(event, handler)
    }
    this.listeners = []
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
}
